'use client';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { refreshPassword } from '@/api/auth';
import { checkEmail } from '@/api/payments';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslation } from '@/i18n/client';
import { WithLang } from '@/types/general';

import { AuthPopupKeys, AuthProps } from './auth.types';
import { AuthResponse } from './AuthResponse';

interface ResetFormValues {
    email: string;
}

export const PasswordRefresh = (props: WithLang<AuthProps>) => {
    const { t } = useTranslation(props.lang, 'auth');

    const [isResponseSuccess, setIsResponseSuccess] = useState<null | boolean>(null);

    const {
        register,
        handleSubmit,
        watch,
        setError,
        clearErrors,
        formState: { errors },
        getValues,
    } = useForm<ResetFormValues>();

    const onSubmit = async (data: ResetFormValues) => {
        const resetUrl = `${window.location.origin}/${props.lang}/serbia?locale=${props.lang}`;

        const response = await refreshPassword(props.lang, data.email, resetUrl);
        setIsResponseSuccess(response);
    };

    const responseContent = isResponseSuccess
        ? {
              title: t('titles.reset'),
              message: t('messages.refreshPasswordSuccess', { email: getValues('email') }),
          }
        : {
              title: t('titles.reset'),
              message: t('messages.resetPasswordFailed'),
          };

    const onRedirect = (key: AuthPopupKeys) => {
        setIsResponseSuccess(null);
        props.onRedirect(key);
    };
    const emailLive = watch('email');
    const debouncedEmail = useDebounce(emailLive, 400);

    const onValidateEmail = async () => {
        if (!debouncedEmail) {
            clearErrors();
            return;
        }
        const isValid = await checkEmail(debouncedEmail);
        if (!isValid) {
            setError('email', { type: 'validate', message: t('errors.email') });
        } else {
            clearErrors();
        }
    };

    const isValid = Boolean(!watch('email') || Object.values(errors).some((err) => err));

    useEffect(() => {
        onValidateEmail();
    }, [debouncedEmail]);

    return isResponseSuccess === null ? (
        <>
            <p className="whitespace-nowrap text-2xl xs:text-xl">{t('titles.reset')}</p>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-y-4 border-b border-opacity-30 pb-6">
                <div className="flex flex-col">
                    <input
                        {...register('email', { required: true })}
                        className="input-primary"
                        placeholder={t('placeholders.email')}
                    ></input>
                    {errors.email && <span className="text-sm text-red-500">{errors.email.message}</span>}
                </div>
                <button
                    type="submit"
                    disabled={isValid}
                    onSubmit={handleSubmit(onSubmit)}
                    className="btn-accent mt-4 py-2 text-base font-semibold"
                >
                    {t('buttons.send')}
                </button>
            </form>
            <div className="mx-auto flex w-full flex-col items-stretch justify-center gap-x-2 gap-y-3 xs:w-5/6 md:flex-row md:items-center">
                <button
                    onClick={() => onRedirect(AuthPopupKeys.LOGIN)}
                    className="btn-primary flex-1 border-accent text-sm md:border-solid"
                >
                    {t('buttons.loginFull')}
                </button>
            </div>
        </>
    ) : (
        <AuthResponse
            lang={props.lang}
            isSuccess={isResponseSuccess}
            onClose={props.onClose}
            responseContent={responseContent}
            onRedirect={(key) => onRedirect(key)}
        />
    );
};
